import type { SxProps, Theme } from "@mui/material/styles";
import { alpha, keyframes } from "@mui/material/styles";

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const pageStack: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: { xs: theme.spacing(2.25), md: theme.spacing(3) },
});

export const desktopHeaderBlock: SxProps<Theme> = {
  display: { xs: "none", md: "flex" },
  flexDirection: "column",
  gap: 1,
};

export const backLink: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  fontSize: "0.8125rem",
  color: "text.secondary",
  textDecoration: "none",
  width: "fit-content",
  transition: "color 0.2s ease",
  "&:hover": {
    color: "primary.main",
  },
};

export const pageTitle: SxProps<Theme> = {
  fontSize: { xs: "1.375rem", md: "1.6875rem" },
  fontWeight: 700,
};

export const pageSubtitle: SxProps<Theme> = {
  fontSize: "0.875rem",
  color: "text.secondary",
};

export const mobileHeaderBar: SxProps<Theme> = (theme) => ({
  display: { xs: "flex", md: "none" },
  alignItems: "center",
  gap: 1.5,
  bgcolor: "background.paper",
  borderBottom: `1px solid ${theme.palette.divider}`,
  mx: theme.spacing(-2),
  mt: theme.spacing(-2),
  px: 2.25,
  py: 1.75,
  mb: 0.5,
});

export const mobileIconButton: SxProps<Theme> = (theme) => ({
  width: 34,
  height: 34,
  borderRadius: 1.25,
  border: `1px solid ${theme.palette.divider}`,
  bgcolor: "background.paper",
  color: "text.secondary",
  flexShrink: 0,
});

export const mobileHeaderTitle: SxProps<Theme> = {
  fontSize: "1rem",
  fontWeight: 700,
};

export const layout: SxProps<Theme> = (theme) => ({
  display: "grid",
  gridTemplateColumns: { xs: "1fr", lg: "1.5fr 1fr" },
  gap: theme.spacing(3.5),
  alignItems: "start",
  width: "100%",
});

export const formColumn: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
});

export const formCard: SxProps<Theme> = (theme) => ({
  display: { xs: "contents", md: "flex" },
  flexDirection: "column",
  gap: 3.5,
  bgcolor: { md: "background.paper" },
  border: { md: `1px solid ${theme.palette.divider}` },
  borderRadius: { md: 3 },
  boxShadow: { md: theme.shadows[3] },
  p: { md: theme.spacing(4.25, 4) },
});

export const mobileFormStack: SxProps<Theme> = () => ({
  display: "flex",
  flexDirection: "column",
  gap: 2.5,
  pb: "calc(160px + env(safe-area-inset-bottom, 0px))",
});

export const desktopFormStack: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 3.5,
};

export const divider: SxProps<Theme> = (theme) => ({
  height: 1,
  bgcolor: theme.palette.divider,
  border: "none",
  m: 0,
});

export const stepHeader: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
};

export const stepNumber: SxProps<Theme> = (theme) => ({
  width: 26,
  height: 26,
  borderRadius: "50%",
  bgcolor: alpha(theme.palette.primary.main, 0.12),
  color: "primary.main",
  fontSize: "0.78125rem",
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

export const stepTitle: SxProps<Theme> = {
  fontSize: { xs: "0.90625rem", md: "0.96875rem" },
  fontWeight: 700,
};

export const stepSection: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: { xs: 2, md: 2.25 },
};

export const imageRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: { xs: 1.75, md: 2.25 },
};

export const imageSlot =
  (size: number, radius: number): SxProps<Theme> =>
  (theme) => ({
    width: size,
    height: size,
    borderRadius: radius / 8,
    border: `1px dashed ${theme.palette.divider}`,
    bgcolor: theme.palette.background.default,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "hidden",
    cursor: "pointer",
    transition: "border-color 0.2s ease",
    "&:hover": {
      borderColor: alpha(theme.palette.primary.main, 0.35),
    },
  });

export const imagePreview =
  (size: number, radius: number): SxProps<Theme> =>
  (theme) => ({
    width: size,
    height: size,
    objectFit: "cover",
    borderRadius: radius / 8,
    border: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    cursor: "pointer",
  });

export const imagePlaceholderText: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "text.disabled",
  textAlign: "center",
  px: 1,
  lineHeight: 1.4,
};

export const imageMeta: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: { xs: 0.75, md: 1 },
  minWidth: 0,
};

export const imageTitle: SxProps<Theme> = {
  fontSize: { xs: "0.78125rem", md: "0.84375rem" },
  fontWeight: 600,
};

export const imageHint: SxProps<Theme> = {
  display: { xs: "none", md: "block" },
  fontSize: "0.75rem",
  color: "text.secondary",
  lineHeight: 1.7,
};

export const ghostLink: SxProps<Theme> = {
  appearance: "none",
  WebkitAppearance: "none",
  outline: "none",
  border: "none",
  background: "none",
  padding: 0,
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.625,
  fontSize: { xs: "0.8125rem", md: "0.78125rem" },
  fontWeight: 600,
  color: "primary.main",
  cursor: "pointer",
  width: "fit-content",
  transition: "opacity 0.2s ease",
  "&:hover": {
    opacity: 0.85,
  },
};

export const iconPickerRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: { xs: 0.875, md: 1 },
  flexWrap: "wrap",
  animation: `${fadeSlideIn} 0.25s ease`,
};

export const iconTilePick =
  (selected: boolean, size: number): SxProps<Theme> =>
  (theme) => ({
    width: size,
    height: size,
    borderRadius: size <= 40 ? 1.375 : 1.625,
    bgcolor: selected ? alpha(theme.palette.primary.main, 0.12) : theme.palette.divider,
    border: `2px solid ${selected ? theme.palette.primary.main : "transparent"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    transition: "all 0.2s ease",
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, 0.12),
    },
  });

export const optionalLinksRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: { xs: 2, md: 2.25 },
  flexWrap: "wrap",
};

export const stockRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  flexWrap: "wrap",
};

export const stockLabel: SxProps<Theme> = {
  fontSize: { xs: "0.8125rem", md: "0.84375rem" },
  fontWeight: 600,
};

export const stockHint: SxProps<Theme> = {
  display: { xs: "none", md: "block" },
  fontSize: "0.71875rem",
  color: "text.secondary",
};

export const formActions: SxProps<Theme> = (theme) => ({
  display: { xs: "none", md: "flex" },
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.75,
  borderTop: `1px solid ${theme.palette.divider}`,
  pt: 2.5,
});

export const submitHint: SxProps<Theme> = {
  textAlign: "center",
  fontSize: { xs: "0.6875rem", md: "0.75rem" },
  color: "text.disabled",
  mt: { xs: 0, md: -1.75 },
};

export const cancelButton: SxProps<Theme> = {
  minWidth: 110,
  fontWeight: 600,
  fontSize: "0.875rem",
};

export const previewColumn: SxProps<Theme> = {
  display: { xs: "none", md: "flex" },
  flexDirection: "column",
  gap: 2.5,
  position: { md: "static", lg: "sticky" },
  top: { lg: 84 },
};

export const previewCard: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 2.5,
  boxShadow: theme.shadows[1],
  overflow: "hidden",
});

export const previewAccent: SxProps<Theme> = (theme) => ({
  height: 6,
  bgcolor: theme.palette.primary.main,
});

export const previewBody: SxProps<Theme> = {
  p: 2.75,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

export const previewHeader: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

export const previewProductRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
};

export const previewName: SxProps<Theme> = {
  fontSize: "0.96875rem",
  fontWeight: 700,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const categoryPill: SxProps<Theme> = (theme) => ({
  alignSelf: "flex-start",
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: "primary.main",
  bgcolor: alpha(theme.palette.primary.main, 0.08),
  px: 1.25,
  py: 0.375,
  borderRadius: 2.5,
});

export const previewRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

export const previewLabel: SxProps<Theme> = {
  fontSize: "0.78125rem",
  color: "text.secondary",
};

export const previewValue: SxProps<Theme> = {
  fontSize: "1rem",
  fontWeight: 800,
};

export const benefitsCard: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 2.5,
  boxShadow: theme.shadows[1],
  p: 2.75,
  display: "flex",
  flexDirection: "column",
  gap: 1.75,
});

export const benefitRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.25,
};

export const benefitText: SxProps<Theme> = {
  fontSize: "0.78125rem",
  color: "text.secondary",
  lineHeight: 1.75,
};

export const mobileCompactPreview: SxProps<Theme> = (theme) => ({
  display: { xs: "flex", md: "none" },
  flexDirection: "column",
  gap: 1.25,
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 2,
  boxShadow: theme.shadows[1],
  p: theme.spacing(1.75, 2),
});

export const mobilePreviewRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
};

export const mobilePreviewName: SxProps<Theme> = {
  fontSize: "0.8125rem",
  fontWeight: 700,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const mobilePreviewCategory: SxProps<Theme> = {
  fontSize: "0.6875rem",
  color: "text.secondary",
};

export const mobilePreviewPrice: SxProps<Theme> = {
  fontSize: "0.84375rem",
  fontWeight: 700,
};

export const stickyCta: SxProps<Theme> = (theme) => ({
  display: { xs: "flex", md: "none" },
  position: "fixed",
  insetInline: 0,
  bottom: "calc(64px + env(safe-area-inset-bottom, 0px))",
  bgcolor: "background.paper",
  borderTop: `1px solid ${theme.palette.divider}`,
  p: theme.spacing(1.75, 2.25, 2.25),
  flexDirection: "column",
  gap: 1,
  zIndex: theme.zIndex.appBar - 1,
  boxShadow: theme.shadows[2],
});

export const hiddenInput: SxProps<Theme> = {
  display: "none",
};

export const reveal: SxProps<Theme> = {
  animation: `${fadeSlideIn} 0.25s ease`,
};

export const successWrap: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 3,
  boxShadow: theme.shadows[3],
  p: { xs: theme.spacing(5, 3), md: theme.spacing(7, 6) },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: { xs: 1.5, md: 1.75 },
  textAlign: "center",
  maxWidth: 460,
  mx: "auto",
});

export const successIconWrap: SxProps<Theme> = (theme) => ({
  width: { xs: 76, md: 84 },
  height: { xs: 76, md: 84 },
  borderRadius: "50%",
  bgcolor: alpha(theme.palette.success.main, 0.1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mb: { xs: 0.5, md: 0.75 },
});

export const successTitle: SxProps<Theme> = {
  fontSize: { xs: "1.0625rem", md: "1.25rem" },
  fontWeight: 700,
};

export const successDescription: SxProps<Theme> = {
  fontSize: { xs: "0.78125rem", md: "0.84375rem" },
  color: "text.secondary",
  lineHeight: 1.8,
};

export const successActionsDesktop: SxProps<Theme> = {
  display: { xs: "none", md: "flex" },
  alignItems: "center",
  gap: 1.25,
  mt: 1.5,
  width: "100%",
};

export const successActionsMobile: SxProps<Theme> = {
  display: { xs: "flex", md: "none" },
  flexDirection: "column",
  gap: 1,
  mt: 1.25,
  width: "100%",
};

export const categoryModalPaper: SxProps<Theme> = (theme) => ({
  borderRadius: 2.75,
  boxShadow: theme.shadows[5],
  width: "100%",
  maxWidth: 380,
  p: 3,
});

export const categoryModalHeader: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

export const categoryModalTitleRow: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
};

export const categoryModalTitle: SxProps<Theme> = {
  fontSize: "1rem",
  fontWeight: 700,
};

export const categoryModalDescription: SxProps<Theme> = {
  fontSize: "0.78125rem",
  color: "text.secondary",
  lineHeight: 1.7,
  mt: -1,
};

export const categoryModalActions: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
};

export const categoryModalCloseButton: SxProps<Theme> = () => ({
  width: 30,
  height: 30,
  borderRadius: 1.125,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "text.secondary",
  cursor: "pointer",
  transition: "color 0.2s ease",
  "&:hover": {
    color: "primary.main",
  },
});

export const mobileCheckboxLabel: SxProps<Theme> = {
  display: { xs: "block", md: "none" },
};

export const desktopCheckboxLabel: SxProps<Theme> = {
  display: { xs: "none", md: "block" },
};

export const loadingWrap: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 320,
};

export const notFoundWrap: SxProps<Theme> = (theme) => ({
  bgcolor: "background.paper",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 3,
  boxShadow: theme.shadows[3],
  p: { xs: 4, md: 6 },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 1.5,
  textAlign: "center",
  maxWidth: 460,
  mx: "auto",
});

export const skuField: SxProps<Theme> = {
  direction: "ltr",
};
